/* global amplitude */
import axios from 'axios'

export const APP_NAME = 'Intento.Web.Axios'
export const APP_VERSION = '1.1.0'

const host = global.INTENTO_CONSOLE_URL || process.env.INTENTO_CONSOLE_URL
const marker = host.replace(/[a-z.:/]+/g, '')

async function getIntentLanguages(slug) {
    const config = {
        url: `https://${process.env.INTENTO_API_HOST}/${slug}/languages`,
        headers: {
            apikey: process.env.INTENTO_API_KEY,
            'x-user-agent': 'Intento.Web.Axios/1.0.1',
        },
    }

    let response = await axios(config)

    let languages = []
    if (response.data) {
        languages = response.data.map(langToOption).sort(sortByKey('label'))
    }

    return languages
}

function getCookieValue(name) {
    if (!global.document) {
        return
    }
    const result = global.document.cookie.match(`\\b${name}=([^;]*)\\b`)
    if (!result) {
        return undefined
    }
    const [, value] = result
    // email as cookie value is double quoted
    // the regexp above puts email with leading quote in result[1]
    if (value[0] === '"') {
        return value.slice(1)
    }
    return value
}

function getMarkedCookieValue(name) {
    return getCookieValue(`${name}${marker}`)
}

function trackedClick(msg, options, callback) {
    return (/* event */) => {
        track(msg, options, callback)
    }
}

function track(event, args, callback) {
    if (typeof args === 'undefined') {
        args = {}
    }
    if (typeof callback !== 'function') {
        callback = () => {}
    }

    args.version = APP_VERSION

    if (process.env.NODE_ENV === 'development') {
        console.log('track', event, args) // eslint-disable-line no-console
        callback()
        return
    }

    if (typeof amplitude !== 'undefined') {
        try {
            amplitude.getInstance().logEvent(event, args, callback)
        } catch (e) {
            if (process.env.NODE_ENV === 'development') {
                console.log(e) // eslint-disable-line no-console
            }
        }
    } else {
        callback()
    }
}

const langToOption = ({ iso_name: label, intento_code: value }) => ({
    value,
    label,
})

/**
 * generates function to sort an array of objects by one of the object keys
 *
 * @param {string} key
 * @returns {function} sorting hat
 */
function sortByKey(key) {
    return (a, b) => {
        const nameA = a[key].toUpperCase() // ignore upper and lowercase
        const nameB = b[key].toUpperCase() // ignore upper and lowercase
        if (nameA < nameB) {
            return -1
        }
        if (nameA > nameB) {
            return 1
        }

        // names must be equal
        return 0
    }
}

/**
 * generates function to sort an array of objects by one of the object keys
 *
 * @param {string} key
 * @param {string} otherKey second key to sort with
 * @returns {function} sorting hat
 */
function sortByKeys(key, otherKey) {
    return (a, b) => {
        const nameA = a[key].toUpperCase() // ignore upper and lowercase
        const nameB = b[key].toUpperCase() // ignore upper and lowercase
        if (nameA < nameB) {
            return -1
        }
        if (nameA > nameB) {
            return 1
        }

        // names must be equal

        const otherNameA = a[otherKey].toUpperCase() // ignore upper and lowercase
        const otherNameB = b[otherKey].toUpperCase() // ignore upper and lowercase
        if (otherNameA < otherNameB) {
            return -1
        }
        if (otherNameA > otherNameB) {
            return 1
        }

        // other names must be equal
        return 0
    }
}

/**
 * generates function to sort an array of objects by one of the object boolean keys
 *
 * @param {string} key
 * @returns {function} sorting hat
 */
function sortByBooleanKey(key) {
    return (a, b) => {
        const nameA = a[key] === undefined ? -1 : Number(a[key])
        const nameB = a[key] === undefined ? -1 : Number(b[key])
        // true goes first, then false and null, undefined goes last
        return nameB - nameA
    }
}

function serialize(params) {
    // convert object to list -- to enable .map
    const keys = Object.keys(params)
    // encode every parameter (unpack list into 2 variables)
    const data = keys.map(
        key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
    )
    // combine into string
    return data.join('&')
}

/**
 * 123456 -> '123 456'
 *
 * @param {number} number
 * @returns {string}
 */
function formatNumberWithSpaces(number) {
    return number.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1 ')
}

function countProviderModels(provider, onlyValue = false) {
    const { id, intent, stock_model } = provider

    const slug = id || intent || ''
    // we are able to count models only for text-related intents -- for now
    if (!slug) {
        return ''
    }
    if (slug.indexOf('ai.text') === -1) {
        return ''
    }
    if (slug.indexOf('ai.text.classify') !== -1) {
        // a lot of providers has no info on languages
        return ''
    }

    if (!stock_model) {
        return onlyValue ? '0' : '0 models'
    }

    const {
        pairs = [],
        symmetric = [],
        lang = [],
        language,
        fromscript,
        toscript,
    } = provider.languages ? provider.languages : provider

    let count = pairs.length

    if (symmetric.length === 1) {
        count += 1
    } else if (symmetric.length > 1) {
        count += symmetric.length * (symmetric.length - 1)
    }

    if (lang.length === 1) {
        count += 1
    } else if (lang.length > 1) {
        count += lang.length * (lang.length - 1)
    }

    if (language) {
        const lang = new Set(language || [])
        count += lang.size
    }
    if (fromscript && toscript) {
        const from = new Set(fromscript || [])
        const to = new Set(toscript || [])
        count += from.size * to.size
    }

    if (count === 0) {
        // return onlyValue ? 1 : '1 model' // TODO
        return ''
    }

    if (count === 1) {
        return onlyValue ? 1 : '1 model'
    }

    count = formatNumberWithSpaces(count)
    return onlyValue ? count : `${count} models`
}

function countProviderLanguages(provider) {
    const {
        pairs,
        symmetric,
        language,
        fromscript,
        toscript,
    } = provider.languages ? provider.languages : provider
    if (pairs || symmetric) {
        return formatNumberWithSpaces(
            (symmetric || []).length + (pairs || []).length
        )
    }
    if (language || fromscript || toscript) {
        const lang = new Set(language || [])
        const from = new Set(fromscript || [])
        const to = new Set(toscript || [])
        return formatNumberWithSpaces(lang.size + from.size + to.size)
    }
    return 0
}

const DEFAULT_VALUES = {
    DEFAULT_IMAGE: '',
    DEFAULT_TEXT:
        "API economy (application programming interface economy) is a general term that describes the way application programming interfaces (APIs) can positively affect an organization's profitability. An API is a customer interface for technology products that allows software components to communicate.",
    DEFAULT_LANG: 'en',
    DEFAULT_LANG_LABEL: 'English',
    DEFAULT_FROM: 'en',
    DEFAULT_FROM_LABEL: 'English',
    DEFAULT_TO: 'es',
    DEFAULT_TO_LABEL: 'Spanish',
}

function getDefaultText(slug) {
    switch (slug) {
        case 'ai/image/ocr':
            return DEFAULT_VALUES.DEFAULT_IMAGE
        case 'ai/text/detect-intent':
            // https://github.com/intento/api_definitions/blob/master/api_data/intent_ai_text_detect_intent.json#L37
            return 'I want to book a flight ticket to Moscow'
        case 'ai/text/dictionary':
            return 'time'
        case 'ai/text/sentiment':
            // https://github.com/intento/api_definitions/blob/master/api_data/intent_ai_text_sentiment.json#L78
            return 'We love this trail and make the trip every year. The views are breathtaking and well worth the hike!'
        case 'ai/text/detect-language':
        case 'ai/text/translate':
            return DEFAULT_VALUES.DEFAULT_TEXT
        case 'ai/text/transliterate':
            // https://github.com/intento/api_definitions/blob/master/api_data/intent_ai_text_transliterate.json#L23
            return 'こんにちは'
        default:
            return ''
    }
}
const EN = {
    value: 'en',
    label: 'English',
}
const ES = {
    value: 'es',
    label: 'Spanish',
}
const RU = {
    value: 'ru',
    label: 'Russian',
}
const JA = {
    value: 'ja',
    label: 'Japanese',
}

const LATN = {
    value: 'latn',
    label: 'latn',
}
const JAPN = {
    value: 'jpan',
    label: 'jpan',
}

function getDefaultFrom(slug) {
    switch (slug) {
        case 'ai/image/ocr':
        case 'ai/text/detect-intent':
        case 'ai/text/dictionary':
        case 'ai/text/sentiment':
        case 'ai/text/translate':
        case 'ai/text/transliterate':
        default:
            return EN
    }
}

function getDefaultTo(slug) {
    switch (slug) {
        // case 'ai/image/ocr':
        // case 'ai/text/detect-intent':
        case 'ai/text/dictionary':
            return RU
        // case 'ai/text/sentiment':
        case 'ai/text/translate':
        case 'ai/text/transliterate':
        default:
            return ES
    }
}

function getDefaultLang(slug) {
    switch (slug) {
        case 'ai/image/ocr':
        case 'ai/text/detect-intent':
        case 'ai/text/dictionary':
        case 'ai/text/sentiment':
        case 'ai/text/translate':
            return EN
        case 'ai/text/transliterate':
            return JA
        default:
            return EN
    }
}

function getDefaultLanguage(slug) {
    switch (slug) {
        case 'ai/image/ocr':
        case 'ai/text/detect-intent':
        case 'ai/text/dictionary':
        case 'ai/text/sentiment':
        case 'ai/text/translate':
            return EN
        case 'ai/text/transliterate':
            return JA
        default:
            return EN
    }
}

function getDefaultFromscript(slug) {
    switch (slug) {
        // case 'ai/image/ocr':
        // case 'ai/text/detect-intent':
        // case 'ai/text/dictionary':
        // case 'ai/text/sentiment':
        // case 'ai/text/translate':
        case 'ai/text/transliterate':
        default:
            return JAPN
    }
}

function getDefaultToscript(slug) {
    switch (slug) {
        // case 'ai/image/ocr':
        // case 'ai/text/detect-intent':
        // case 'ai/text/dictionary':
        // case 'ai/text/sentiment':
        // case 'ai/text/translate':
        case 'ai/text/transliterate':
        default:
            return LATN
    }
}

function defaultsWereChanged(slug, capabilities) {
    if (slug === 'ai/text/translate' || slug === 'ai/text/dictionary') {
        const { from, to, text } = capabilities
        return (
            (from.value && from.value !== getDefaultFrom(slug).value) ||
            (to.value && to.value !== getDefaultTo(slug).value) ||
            (text && text !== getDefaultText(slug))
        )
    }

    if (slug === 'ai/text/transliterate') {
        const { fromscript, toscript, language, text } = capabilities
        return (
            (fromscript.value &&
                fromscript.value !== getDefaultFromscript(slug).value) ||
            (toscript.value &&
                toscript.value !== getDefaultToscript(slug).value) ||
            (language.value &&
                language.value !== getDefaultLanguage(slug).value) ||
            (text && text !== getDefaultText(slug))
        )
    }

    const langIntents = [
        'ai/text/sentiment',
        'ai/text/classify',
        'ai/text/detect-intent',
    ]
    if (langIntents.indexOf(slug) !== -1) {
        const { lang, text } = capabilities
        return (
            (lang.value && lang.value !== getDefaultLang(slug).value) ||
            (text && text !== getDefaultText(slug))
        )
    }

    const { text } = capabilities
    return text && text !== getDefaultText(slug)
}

function getProcessedResults(slug, response = {}) {
    const result = (response.response || {}).results || []

    if (slug === 'ai/image/tagging' || slug === 'ai/text/detect-language') {
        return result
    }

    if (typeof result === 'string') {
        return result || 'Empty response' // for a 'ai/text/transliterate' string is expected
    }

    if (slug === 'ai/text/translate') {
        return result.join(' ') || 'Empty response'
    }

    // dictionary -> {}
    // detect-intent, sentiment -> list of dictionaries
    return JSON.stringify(result, null, 4) || 'Empty response'
}

const providerSupportsLanguagePair = (from, to) => provider => {
    const providerSupportsFrom = from
        ? provider.languages.symmetric.indexOf(from) !== -1
        : provider.lang_detect
    const providerSupportsTo = provider.languages.symmetric.indexOf(to) !== -1

    if (providerSupportsFrom && providerSupportsTo) {
        return true
    }

    if (
        provider.languages.pairs.filter(
            pair => pair.from === from && pair.to === to
        ).length > 0
    ) {
        return true
    }

    return false
}

function getFilteredByCapabilities(slug, providers, capabilities) {
    let list = [...providers]
    // list = list.filter(
    //     provider => provider.stock_model && provider.integrated
    // )

    if (slug === 'ai/text/sentiment') {
        const { lang } = capabilities
        if (lang.value) {
            list = list.filter(provider => {
                // TODO theysay
                if (
                    (provider.languages.lang || []).indexOf(lang.value) !== -1
                ) {
                    return true
                }
                return false
            })
        } else {
            list = list.filter(provider => provider.lang_detect)
        }
    }

    if (slug === 'ai/text/translate') {
        const { to, from } = capabilities
        list = list.filter(providerSupportsLanguagePair(from.value, to.value))
    }

    return list.sort(sortByBooleanKey('showBolt'))
}

function getSupportedLanguages(provider) {
    let supportedLanguages = []
    const {
        lang,
        // language,
        pairs,
        symmetric,
        // toscript,
        // fromscript,
    } = provider.languages || provider

    if (lang) {
        supportedLanguages = [...supportedLanguages, ...lang]
    }
    if (symmetric) {
        supportedLanguages = [...supportedLanguages, ...symmetric]
    }
    if (pairs) {
        supportedLanguages = [
            ...supportedLanguages,
            ...pairs.map(pair => pair.from),
            ...pairs.map(pair => pair.to),
        ]
    }
    const uniqueLanguages = new Set(supportedLanguages)
    // console.log(provider.id);
    // console.log([...uniqueLanguages].join(', '))
    return [...uniqueLanguages]
}

function capitalize(str = '') {
    return str.replace(/\b\w/g, l => l.toUpperCase())
}

function getResultId({ slug, intent, capabilities }) {
    if (slug === 'ai/text/translate' || slug === 'ai/text/dictionary') {
        let { to, from } = capabilities
        if (from === null || from === undefined) {
            from = {}
        }
        if (to === null || to === undefined) {
            to = {}
        }
        return `${from.value || ''}-${to.value || ''}`
    }

    if (slug === 'ai/text/transliterate') {
        let { from, fromscript, toscript } = capabilities
        if (from === null || from === undefined) {
            from = {}
        }
        if (fromscript === null || fromscript === undefined) {
            fromscript = {}
        }
        if (toscript === null || toscript === undefined) {
            toscript = {}
        }
        return `${from.value || ''}-${fromscript.value || ''}-${
            toscript.value || ''
        }`
    }

    if (intent.hasImageInput) {
        return 'image'
    }

    if (intent.hasLanguages) {
        let { lang } = capabilities
        if (lang === null || lang === undefined) {
            lang = {}
        }
        return lang.value || ''
    }

    return 'common'
}

export {
    track,
    trackedClick,
    getCookieValue,
    getMarkedCookieValue,
    langToOption,
    sortByKey,
    sortByKeys,
    serialize,
    formatNumberWithSpaces,
    countProviderModels,
    countProviderLanguages,
    defaultsWereChanged,
    DEFAULT_VALUES,
    getProcessedResults,
    getFilteredByCapabilities,
    getDefaultText,
    getDefaultFrom,
    getDefaultTo,
    getDefaultLang,
    getDefaultLanguage,
    getDefaultFromscript,
    getDefaultToscript,
    getSupportedLanguages,
    capitalize,
    getResultId,
    getIntentLanguages
}



// WEBPACK FOOTER //
// /usr/src/app/frontend/src/utils/index.js